<script setup>
import dayjs from 'dayjs'
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore, infoBoxIdEnum } from '@/stores/portal.store'

import { useIsUpdated } from '@/composables/object'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useClone } from '@/composables/object'
import { dateRangePresetDefinitions } from '@/composables/date-range-presets'
import { usePrintTemplates } from '@/composables/print-template'
import { useReportColumnTemplates } from '@/composables/report-column-template'
import { isValidPaymentDays, paymentDaysLabel } from '@/composables/invoice'

import TimeSelector from '@/components/shared/TimeSelector.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import CountrySelect from '@/components/shared/CountrySelect.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import WarehouseSelector from '@/components/shared/WarehouseSelector.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import VatSelect from '@/components/shared/VatSelect.vue'
import DocumentTermTemplateSelect from '@/components/accounting/DocumentTermTemplateSelect.vue'
import CompanyExchangeRates from '@/components/accounting/CompanyExchangeRates.vue'
import PermissionsMatrix from '@/components/accounting/PermissionsMatrix.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PrintTemplateSelect from '@/components/accounting/PrintTemplateSelect.vue'
import PrintTemplatesManager from '@/components/accounting/PrintTemplatesManager.vue'
import ReportColumnTemplatesManager from '@/components/accounting/ReportColumnTemplatesManager.vue'
import TaskTypeSettings from '@/components/task/TaskTypeSettings.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'
import AvatarImage from '@/components/shared/AvatarImage.vue'
import ExpenseItemSettings from '@/components/accounting/ExpenseItemSettings.vue'
import StaffExpenseProfile from '@/components/accounting/StaffExpenseProfile.vue'
import NotificationSettings from '@/components/NotificationSettings.vue'

const i18n = getI18n()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const durationDaysOptions = computed(() => portalStore.getLocalizedDurationDays())
webSiteStore.fabActions = []

const { isMobile } = useDetectDevice()
const { notifySuccess, notifyWarning } = useNotify()

const route = useRoute()

const { companyDataTabEnum } = accountingStore
const isTabAvailable = (value) => {
  switch (value) {
    case companyDataTabEnum.EXCHANGE_RATES: return webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)
    default: return Object.values(companyDataTabEnum).includes(value)
  }
}
const tabFromQuery = (queryTab) => {
  const requestedTab = Number(queryTab)
  return queryTab != null && isTabAvailable(requestedTab) ? requestedTab : companyDataTabEnum.GENERAL
}
const tab = ref(tabFromQuery(route.query.tab))
watch(() => route.query.tab, (queryTab) => { tab.value = tabFromQuery(queryTab) })

const TIME_FIELDS = Object.freeze(['default_start_time', 'default_end_time', 'start_scheduler_time', 'end_scheduler_time', 'night_start_time', 'night_end_time'])
const convertTimeFieldsToISO = (data) => {
  TIME_FIELDS.forEach((field) => { data[field] = dayjs(`1970-01-01T${data[field]}`).toISOString() })
}
const convertTimeFieldsToTime = (data) => {
  TIME_FIELDS.forEach((field) => { data[field] = dayjs(data[field]).format('HH:mm:ss') })
}

const companyData = ref(useClone(accountingStore.companyData))
convertTimeFieldsToISO(companyData.value)
if (!companyData.value.scheduler_cell_duration) {
  companyData.value.scheduler_cell_duration = 30
}
const { isUpdated, original, setOriginal } = useIsUpdated(companyData)

const repairSliderIndex = computed({
  get: () => {
    const idx = durationDaysOptions.value.findIndex(o => o.value === companyData.value.repair_on_time_days)
    return idx >= 0 ? idx : durationDaysOptions.value.findIndex(o => o.value === 30)
  },
  set: (idx) => { companyData.value.repair_on_time_days = durationDaysOptions.value[idx]?.value ?? 30 },
})

const recentMovementDaysLabel = computed(() =>
  companyData.value.search_recent_movement_days === 0
    ? i18n.t('common.disabled')
    : `${companyData.value.search_recent_movement_days} ${i18n.t('common.day', companyData.value.search_recent_movement_days)}`
)

const timelinePeriodOptions = computed(() =>
  dateRangePresetDefinitions
    .filter(p => p.selectable)
    .map(p => ({
      value: p.value,
      label: p.labelCount != null ? i18n.t(p.labelKey, p.labelCount) : i18n.t(p.labelKey),
    }))
)

// Grid width per breakpoint: `col(mobile, desktop)` — every row of fields adds up to 12.
const col = (mobile, desktop) => `col-${isMobile.value ? mobile : desktop}`

const onCountryChange = () => {
  companyData.value.city = null
  companyData.value.zip_code = null
  companyData.value.street = null
  companyData.value.street_number = null
}

const logoFiles = ref(
  companyData.value.logo
    ? [{ name: companyData.value.logo.split('/').pop() }]
    : []
)
const logoUrls = ref(
  companyData.value.logo_url
    ? [{ url: companyData.value.logo_url }]
    : []
)
const logoChanged = computed(() =>
  logoFiles.value.some(f => f.payload) ||
  (logoFiles.value.length === 0 && !!companyData.value.logo) ||
  (logoUrls.value[0]?.url || null) !== (companyData.value.logo_url || null)
)

const toProfile = (user) => ({
  first_name: user.first_name,
  last_name: user.last_name,
  preferred_language: user.preferred_language,
})
const profile = reactive(toProfile(webSiteStore.user))
const { isUpdated: isProfileUpdated, setOriginal: setProfileOriginal } = useIsUpdated(profile)
const notificationPreferences = ref([])
const { isUpdated: isNotificationPreferencesUpdated, setOriginal: setNotificationPreferencesOriginal } = useIsUpdated(notificationPreferences)

const setNotificationPreferences = (settings) => {
  notificationPreferences.value = settings
  setNotificationPreferencesOriginal(settings)
}
const userAvatarImage = computed(() => webSiteStore.user.avatar ? `data:image/png;base64,${webSiteStore.user.avatar}` : '')
const avatarEdit = reactive({ avatar_file: undefined })
const avatarChanged = computed(() => avatarEdit.avatar_file !== undefined)
const avatarPreview = computed(() => avatarChanged.value ? avatarEdit.avatar_file?.payload : userAvatarImage.value)
const cameraInput = ref(null)
const profileFullName = computed(() => [profile.first_name, profile.last_name].filter(Boolean).join(' '))

const onAvatarRemove = () => {
  avatarEdit.avatar_file = userAvatarImage.value ? null : undefined
}

const saveProfileAsync = async() => {
  const languageChanged = profile.preferred_language !== webSiteStore.user.preferred_language
  await webSiteStore.updateOwnUserDetailsAsync({
    ...profile,
    ...(avatarChanged.value && { avatar: avatarEdit.avatar_file?.payload ?? null }),
  })
  avatarEdit.avatar_file = undefined
  Object.assign(profile, toProfile(webSiteStore.user))
  setProfileOriginal(profile)
  if (languageChanged) {
    await portalStore.setLanguageAsync(webSiteStore.user.preferred_language)
    portalStore.incrementReloadKey()
  }
}

const isCompanyDataChanged = computed(() => isUpdated.value || logoChanged.value)
const reimbursementProfile = ref(null)
const isReimbursementProfileUpdated = computed(() => !!reimbursementProfile.value?.isUpdated)

const onSave = async() => {
  if (isCompanyDataChanged.value) await saveCompanyDataAsync()
  if (isProfileUpdated.value || avatarChanged.value) await saveProfileAsync()
  if (isReimbursementProfileUpdated.value) await reimbursementProfile.value.saveAsync()
  if (isNotificationPreferencesUpdated.value) setNotificationPreferences(await webSiteStore.saveNotificationSettingsAsync(notificationPreferences.value))
  notifySuccess(i18n.t('info.updatedSuccessfully'))
}

const saveCompanyDataAsync = async() => {
  const updatedData = useClone(companyData.value)

  convertTimeFieldsToTime(updatedData)

  const newLogoFile = logoFiles.value.find(f => f.payload)
  if (newLogoFile) {
    updatedData.logo_payload = { name: newLogoFile.name, payload: newLogoFile.payload }
    updatedData.logo_url = null
  } else if (logoFiles.value.length === 0 && companyData.value.logo) {
    updatedData.logo_payload = null
  }
  updatedData.logo_url = logoUrls.value[0]?.url || null

  await accountingStore.updateCompanyDataAsync(updatedData)
  await accountingStore.fetchCompanyDataAsync(null, true)

  const newData = useClone(accountingStore.companyData)
  convertTimeFieldsToISO(newData)
  companyData.value = newData
  setOriginal(newData)
  logoFiles.value = newData.logo ? [{ name: newLogoFile?.name || newData.logo.split('/').pop() }] : []
  logoUrls.value = newData.logo_url ? [{ url: newData.logo_url }] : []
}

const { invoiceNumberFormatTokens } = accountingStore
const invoiceNumberFormatTokenParams = { year: invoiceNumberFormatTokens.YEAR, number: invoiceNumberFormatTokens.NUMBER }
const WORKING_DAY_HOURS_MIN = 1
const WORKING_DAY_HOURS_MAX = 24
const workingDayHoursRule = (value) => {
  if (value < WORKING_DAY_HOURS_MIN) return i18n.t('error.validation.minValue', { min: WORKING_DAY_HOURS_MIN })
  if (value > WORKING_DAY_HOURS_MAX) return i18n.t('error.validation.maxValue', { max: WORKING_DAY_HOURS_MAX })
  return true
}

const invoiceNumberFormatRules = [
  (value) => !!value?.includes(invoiceNumberFormatTokens.NUMBER) || i18n.t('error.validation.numberFormatWithoutNumber'),
]

const vatTypes = computed(() => [...(webSiteStore.getLocalizedVatTypes() || [])].sort((a, b) => b.percentage - a.percentage))
const vatPercentageDisplay = (percentage) => Number((Number(percentage) * 100).toFixed(6))

const newVatPercentage = ref(null)
const isDuplicateVatPercentage = computed(() =>
  newVatPercentage.value !== null &&
  newVatPercentage.value !== '' &&
  vatTypes.value.some(vat => vatPercentageDisplay(vat.percentage) === Number(newVatPercentage.value))
)
const canAddVat = computed(() => {
  const percentage = newVatPercentage.value
  return percentage !== null && percentage !== '' && percentage <= 100
})

const onAddVat = async() => {
  if (isDuplicateVatPercentage.value) {
    notifyWarning(i18n.t('error.validation.vatPercentageDuplicate'))
    return
  }
  await webSiteStore.createVatTypeAsync({ percentage: newVatPercentage.value })
  await webSiteStore.fetchVatTypesAsync({ all: true }, true)
  notifySuccess(i18n.t('info.createdSuccessfully'))
  newVatPercentage.value = null
}

const showDeleteVatDialog = ref(false)
const vatToDelete = ref(null)

const onDeleteVat = async(vat) => {
  await webSiteStore.deleteVatTypeAsync(vat.id, { params: { dry_run: true } })
  vatToDelete.value = vat
  showDeleteVatDialog.value = true
}

const onDeleteVatConfirmed = async() => {
  await webSiteStore.deleteVatTypeAsync(vatToDelete.value.id)
  await webSiteStore.fetchVatTypesAsync({ all: true }, true)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
}

const paymentDaysOptions = computed(() => [...(companyData.value.payment_days_options ?? [])].sort((a, b) => a - b))
const newPaymentDays = ref(null)
const canAddPaymentDays = computed(() => isValidPaymentDays(newPaymentDays.value) && !paymentDaysOptions.value.includes(newPaymentDays.value))

const onAddPaymentDays = () => {
  companyData.value.payment_days_options = [...paymentDaysOptions.value, newPaymentDays.value]
  newPaymentDays.value = null
}

const onRemovePaymentDays = (days) => {
  companyData.value.payment_days_options = paymentDaysOptions.value.filter(option => option !== days)
}

const { defaultFields } = usePrintTemplates()
const { QUOTE: QUOTE_DOCUMENT, INVOICE: INVOICE_DOCUMENT, REPORT: REPORT_DOCUMENT } = accountingStore.printDocumentTypeEnum
const canEditCompanySettings = webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)
const printTemplateDefaults = [
  { documentType: QUOTE_DOCUMENT, field: defaultFields[QUOTE_DOCUMENT], label: 'common.printTemplateDefaultForQuotes' },
  { documentType: INVOICE_DOCUMENT, field: defaultFields[INVOICE_DOCUMENT], label: 'common.printTemplateDefaultForInvoices' },
  { documentType: REPORT_DOCUMENT, field: defaultFields[REPORT_DOCUMENT], label: 'common.printTemplateDefaultForReports' },
]
const showPrintTemplatesManager = ref(false)

const { selectableTemplates: selectableColumnTemplates } = useReportColumnTemplates()
const reportColumnTemplateDefaults = accountingStore.getLocalizedPrintSettingTypes().map(type => ({
  reportType: type.id,
  field: accountingStore.reportColumnTemplateDefaultFields[type.id],
  label: type.name,
}))
const columnTemplateOptions = ({ reportType, field }) => selectableColumnTemplates(reportType, companyData.value[field]).map(template => ({ label: template.label, value: template.id }))
const showReportColumnTemplatesManager = ref(false)

const syncDefaults = (fieldDefaults) => {
  const defaults = Object.fromEntries(fieldDefaults.map(({ field }) => [field, accountingStore.companyData[field]]))
  setOriginal({ ...original.value, ...defaults })
  Object.assign(companyData.value, defaults)
}
const isInlineManagerOpen = computed(() => !isMobile.value && (showPrintTemplatesManager.value || showReportColumnTemplatesManager.value))
const onReportColumnTemplatesManagerHide = () => {
  showReportColumnTemplatesManager.value = false
  syncDefaults(reportColumnTemplateDefaults)
}

const locationsSection = ref(null)

onMounted(async() => {
  webSiteStore.fetchVatTypesAsync({ all: true }, true)
  accountingStore.fetchReportColumnTemplatesAsync()
  setNotificationPreferences(await webSiteStore.fetchNotificationSettingsAsync())
  if (Number(route.query.section) === accountingStore.companyDataSectionEnum.LOCATIONS) {
    locationsSection.value?.$el.scrollIntoView({ behavior: 'smooth' })
  }
})

</script>
<template>
  <div :class="{ 'company-data--mobile column': isMobile }">
    <print-templates-manager
      v-if="showPrintTemplatesManager && !isMobile"
      flat
      class="company-data__manager"
      @cancel="showPrintTemplatesManager = false"
      @defaults-changed="syncDefaults(printTemplateDefaults)"
    />
    <report-column-templates-manager
      v-else-if="showReportColumnTemplatesManager && !isMobile"
      flat
      class="company-data__manager"
      @cancel="onReportColumnTemplatesManagerHide"
    />
    <q-card
      v-show="!isInlineManagerOpen"
      flat
      :class="['column no-wrap q-pa-none', isMobile ? 'col' : 'full-height company-data__card--desktop']"
      style="min-height: 0;"
    >
      <div :class="{ 'order-last company-data__tabs': isMobile }">
        <q-separator v-if="isMobile" />
        <q-tabs
          v-model="tab"
          :align="isMobile ? 'justify' : 'left'"
          :class="{ 'text-primary': !isMobile }"
          active-color="primary"
          indicator-color="primary"
          :no-caps="!isMobile"
        >
          <q-tab :name="companyDataTabEnum.GENERAL" :label="i18n.t('common.general')" />
          <q-tab :name="companyDataTabEnum.PERMISSIONS" :label="i18n.t('common.rolesAndPermissions')" />
          <q-tab :name="companyDataTabEnum.ACCOUNTING" :label="i18n.t('common.accounting')" />
          <q-tab
            v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
            :name="companyDataTabEnum.EXCHANGE_RATES"
            :label="i18n.t('common.exchangeRate', 2)"
          />
          <q-tab :name="companyDataTabEnum.PROFILE" :label="i18n.t('common.myProfile')" />
        </q-tabs>
        <q-separator v-if="!isMobile" />
      </div>
      <q-form class="col column no-wrap" style="min-height: 0;">
        <q-tab-panels
          v-model="tab"
          animated
          class="scroll col q-px-sm dialog-card-content"
          style="min-height: 0;"
        >
          <q-tab-panel :name="companyDataTabEnum.GENERAL" class="q-pa-none">
            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-information-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.mainInfo') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <q-input
                  v-model="companyData.name"
                  :label="i18n.t('common.companyName')"
                  :class="col(12, 3)"
                  readonly
                />
                <q-input
                  v-model="companyData.vat_number"
                  :label="i18n.t('common.vatNumber')"
                  :class="col(12, 3)"
                  readonly
                />
                <q-input
                  v-model="companyData.description"
                  :label="i18n.t('common.description')"
                  :class="col(12, 6)"
                  autogrow
                />
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-map-marker-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.address') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <country-select
                  v-model="companyData.country"
                  :class="col(12, 3)"
                  @update:model-value="onCountryChange"
                />
                <q-input
                  v-model="companyData.city"
                  :label="i18n.t('common.city')"
                  :class="col(12, 3)"
                />
                <q-input
                  v-model="companyData.zip_code"
                  :label="i18n.t('common.zipCode')"
                  :class="col(6, 2)"
                />
                <q-input
                  v-model="companyData.street"
                  :label="i18n.t('common.street')"
                  :class="col(6, 3)"
                />
                <q-input
                  v-model="companyData.street_number"
                  :label="i18n.t('common.streetNumber')"
                  :class="col(12, 1)"
                />
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-image-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.logo') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm">
                <file-url-edit
                  class="q-pl-md"
                  :model-value="logoFiles"
                  :urls="logoUrls"
                  single
                  images-only
                  @update:model-value="logoFiles = $event"
                  @update:urls="logoUrls = $event"
                />
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-chart-timeline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.timeline') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md items-center">
                <q-select
                  v-model="companyData.timeline_default_period"
                  :options="timelinePeriodOptions"
                  :label="i18n.t('common.defaultPeriod')"
                  option-value="value"
                  option-label="label"
                  emit-value
                  map-options
                  :class="col(12, 3)"
                />
                <q-toggle
                  v-model="companyData.timeline_default_show_listing_first"
                  :label="i18n.t('common.defaultShowListingFirst')"
                  :class="col(6, 2)"
                />
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-calendar-account"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.staff.holidays') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm">
                <q-toggle
                  v-model="companyData.require_holiday_approval"
                  :label="i18n.t('common.requireHolidayApproval')"
                />
                <div class="text-caption text-grey-7 q-mt-xs">
                  {{ i18n.t('common.requireHolidayApprovalHint') }}
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-calendar-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.scheduler') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <div :class="`${col(12, 3)} q-mb-md`">
                  <div class="q-mb-sm text-subtitle2">{{ i18n.t('common.schedulerCellDuration') }}</div>
                  <q-slider
                    v-model="companyData.scheduler_cell_duration"
                    :min="30"
                    :max="120"
                    :step="30"
                    label-always
                    switch-label-side
                    :label-value="`${companyData.scheduler_cell_duration} min`"
                    markers
                  />
                </div>
                <time-selector
                  v-model="companyData.start_scheduler_time"
                  :label="i18n.t('common.startTime')"
                  :class="col(6, 2)"
                />
                <time-selector
                  v-model="companyData.end_scheduler_time"
                  :label="i18n.t('common.endTime')"
                  :class="col(6, 2)"
                  :min="companyData.start_scheduler_time"
                  end
                />
                <q-select
                  v-model="companyData.rest_days"
                  :options="portalStore.getLocalizedWeekdays()"
                  :label="i18n.t('common.restDays')"
                  option-value="value"
                  option-label="label"
                  multiple
                  emit-value
                  map-options
                  use-chips
                  :class="col(12, 5)"
                />
              </div>
            </q-card-section>


            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-view-dashboard-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.dashboard') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <div :class="isMobile ? 'col-12 q-mb-md' : 'col-3'">
                  <div class="q-mb-sm text-subtitle2">{{ i18n.t('common.repairOnTimeDays') }}</div>
                  <q-slider
                    v-model="repairSliderIndex"
                    :min="0"
                    :max="durationDaysOptions.length - 1"
                    :step="1"
                    label-always
                    switch-label-side
                    :label-value="durationDaysOptions[repairSliderIndex]?.label"
                    markers
                    snap
                  />
                </div>
                <div v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)" :class="isMobile ? 'col-12' : 'col-2'">
                  <q-input
                    v-model.number="companyData.yearly_revenue_target"
                    :label="i18n.t('common.yearlyRevenueTarget')"
                    type="number"
                    min="0"
                    :prefix="webSiteStore.getDefaultCurrency()?.symbol"
                    clearable
                  />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-magnify"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.search') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <div :class="`${col(12, 3)} q-mb-md`">
                  <div class="q-mb-sm text-subtitle2 row items-center no-wrap">
                    <span>{{ i18n.t('common.recentMovementsWindow') }}</span>
                    <info-box popup class="q-ml-xs" :message="i18n.t('common.recentMovementsWindowInfo')" />
                  </div>
                  <q-slider
                    v-model="companyData.search_recent_movement_days"
                    :min="0"
                    :max="30"
                    :step="1"
                    label-always
                    switch-label-side
                    :label-value="recentMovementDaysLabel"
                    :markers="5"
                  />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-settings-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.default', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <currency-select
                  v-model="companyData.default_currency_ref"
                  :class="col(6, 2)"
                />
                <q-input
                  v-model.number="companyData.currency_decimals"
                  type="number"
                  min="0"
                  max="6"
                  :label="i18n.t('common.currencyDecimals')"
                  :class="col(6, 1)"
                />
                <q-select
                  v-model="companyData.default_unit_of_measure_ref"
                  :options="webSiteStore.getLocalizedUnitsOfMeasure()"
                  :label="i18n.t('common.unitOfMeasure')"
                  option-value="id"
                  option-label="name"
                  :class="col(6, 2)"
                />
                <q-select
                  v-model="companyData.default_extra_unit_of_measure_ref"
                  :options="webSiteStore.getLocalizedUnitsOfMeasure()"
                  :label="i18n.t('common.extraUnitOfMeasure')"
                  option-value="id"
                  option-label="name"
                  :class="col(6, 2)"
                />
                <vat-select
                  v-model="companyData.default_vat_type_ref"
                  clearable
                  :class="col(6, 2)"
                />
                <q-input
                  v-model.number="companyData.working_day_hours"
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  suffix="h"
                  :label="i18n.t('common.workingDayHours')"
                  :rules="[workingDayHoursRule]"
                  :class="col(6, 3)"
                >
                  <template #append>
                    <info-box popup :message="i18n.t('common.workingDayHoursHint')" />
                  </template>
                </q-input>
                <time-selector
                  v-model="companyData.default_start_time"
                  :label="i18n.t('common.startTime')"
                  :class="col(6, 1)"
                />
                <time-selector
                  v-model="companyData.default_end_time"
                  :label="i18n.t('common.endTime')"
                  :class="col(6, 1)"
                  :min="companyData.default_start_time"
                  end
                />
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none row items-center">
                <q-icon
                  name="mdi-weather-night"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.nightWindow') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <time-selector
                  v-model="companyData.night_start_time"
                  :label="i18n.t('common.nightWindowStart')"
                  :class="col(6, 2)"
                />
                <time-selector
                  v-model="companyData.night_end_time"
                  :label="i18n.t('common.nightWindowEnd')"
                  :class="col(6, 2)"
                />
                <div class="col-12">
                  <info-box :id="infoBoxIdEnum.NIGHT_WINDOW" :message="i18n.t('common.nightWindowHint')" />
                </div>
              </div>
            </q-card-section>

            <q-card-section ref="locationsSection" class="q-pt-none">
              <q-item-label header class="q-px-none row items-center">
                <q-icon
                  name="mdi-warehouse"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.warehouse', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <warehouse-selector
                  v-model="companyData.default_warehouse"
                  :kind="planningStore.warehouseKindEnum.PHYSICAL"
                  :class="col(12, 4)"
                />
                <warehouse-selector
                  v-model="companyData.default_transit_warehouse"
                  :class="col(12, 4)"
                  :label="i18n.t('common.defaultTransitWarehouse')"
                  :kind="planningStore.warehouseKindEnum.VIRTUAL"
                />
                <warehouse-selector
                  v-model="companyData.default_orders_warehouse"
                  :class="col(12, 4)"
                  :label="i18n.t('common.defaultOrdersWarehouse')"
                  :kind="planningStore.warehouseKindEnum.VIRTUAL"
                />
                <div class="col-12">
                  <info-box :id="infoBoxIdEnum.TRANSIT_ORDERS_VIRTUAL" :message="i18n.t('common.transitOrdersVirtualHint')" />
                </div>
              </div>
            </q-card-section>

            <q-card-section v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)" class="q-pt-none">
              <q-item-label header class="q-px-none row items-center">
                <q-icon
                  name="mdi-shape-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.tasks.taskType', 2) }}
                </span>
              </q-item-label>
              <div :class="{ 'q-ml-sm q-pl-md': !isMobile }">
                <task-type-settings />
              </div>
            </q-card-section>
          </q-tab-panel>

          <q-tab-panel :name="companyDataTabEnum.PERMISSIONS" class="q-pa-none">
            <q-card-section>
              <permissions-matrix />
            </q-card-section>
          </q-tab-panel>

          <q-tab-panel :name="companyDataTabEnum.ACCOUNTING" class="q-pa-none">
            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-file-document-edit-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.printTemplate', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <print-template-select
                  v-for="printTemplateDefault in printTemplateDefaults"
                  :key="printTemplateDefault.field"
                  v-model="companyData[printTemplateDefault.field]"
                  :class="col(12, 4)"
                  :document-type="printTemplateDefault.documentType"
                  :label="i18n.t(printTemplateDefault.label)"
                  :readonly="!canEditCompanySettings"
                />
                <div class="col-12">
                  <info-box :message="i18n.t('info.printTemplateDefaultsHint')" />
                </div>
                <div v-if="canEditCompanySettings" class="col-12">
                  <q-btn
                    flat
                    no-caps
                    color="primary"
                    icon="mdi-cog-outline"
                    :label="i18n.t('common.printTemplatesManage')"
                    @click="showPrintTemplatesManager = true"
                  />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-table-column"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.report.columnTemplate', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <q-select
                  v-for="columnTemplateDefault in reportColumnTemplateDefaults"
                  :key="columnTemplateDefault.field"
                  v-model="companyData[columnTemplateDefault.field]"
                  :class="col(12, 4)"
                  :options="columnTemplateOptions(columnTemplateDefault)"
                  :label="columnTemplateDefault.label"
                  :readonly="!canEditCompanySettings"
                  emit-value
                  map-options
                />
                <div class="col-12">
                  <info-box :id="infoBoxIdEnum.REPORT_COLUMN_TEMPLATE_DEFAULTS" :message="i18n.t('info.reportColumnTemplateDefaultsHint')" />
                </div>
                <div v-if="canEditCompanySettings" class="col-12">
                  <q-btn
                    flat
                    no-caps
                    color="primary"
                    icon="mdi-cog-outline"
                    :label="i18n.t('common.report.columnTemplatesManage')"
                    @click="showReportColumnTemplatesManager = true"
                  />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-percent-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.vat', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm q-pl-md">
                <div v-if="vatTypes.length" class="row items-center q-gutter-sm q-mb-md">
                  <q-chip
                    v-for="vat in vatTypes"
                    :key="vat.id"
                    :removable="vat.id < 0"
                    icon-remove="mdi-delete-outline"
                    color="primary"
                    text-color="white"
                    :label="`${vatPercentageDisplay(vat.percentage)}%`"
                    @remove="onDeleteVat(vat)"
                  />
                </div>
                <div class="row q-col-gutter-md items-center">
                  <q-input
                    v-model.number="newVatPercentage"
                    :class="col(6, 2)"
                    type="number"
                    min="1"
                    max="100"
                    :label="i18n.t('common.percentage')"
                    suffix="%"
                  />
                  <div>
                    <q-btn
                      color="positive"
                      :label="i18n.t('common.add')"
                      :disable="!canAddVat"
                      @click="onAddVat"
                    />
                  </div>
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-calendar-clock"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.paymentDays') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm q-pl-md">
                <div class="row items-center q-gutter-sm q-mb-md">
                  <q-chip
                    v-for="days in paymentDaysOptions"
                    :key="days"
                    :removable="canEditCompanySettings && days !== companyData.default_payment_days"
                    icon-remove="mdi-delete-outline"
                    color="primary"
                    text-color="white"
                    :label="paymentDaysLabel(days)"
                    @remove="onRemovePaymentDays(days)"
                  />
                </div>
                <div v-if="canEditCompanySettings" class="row q-col-gutter-md items-center q-mb-md">
                  <q-input
                    v-model.number="newPaymentDays"
                    :class="col(6, 2)"
                    type="number"
                    min="1"
                    :label="i18n.t('common.day', 2)"
                  />
                  <div>
                    <q-btn
                      color="positive"
                      :label="i18n.t('common.add')"
                      :disable="!canAddPaymentDays"
                      @click="onAddPaymentDays"
                    />
                  </div>
                </div>
                <div class="row q-col-gutter-md">
                  <q-select
                    v-model="companyData.default_payment_days"
                    :options="paymentDaysOptions"
                    :option-label="paymentDaysLabel"
                    :label="i18n.t('common.defaultPaymentDays')"
                    :readonly="!canEditCompanySettings"
                    :class="col(12, 3)"
                  />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-receipt-text-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.invoice', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row q-col-gutter-md">
                <q-input
                  v-model="companyData.invoice_number_format"
                  :label="i18n.t('common.invoiceNumberFormat')"
                  :rules="invoiceNumberFormatRules"
                  maxlength="50"
                  :class="col(12, 3)"
                />
                <q-input
                  v-model.number="companyData.amount_decimals"
                  type="number"
                  min="0"
                  max="6"
                  :label="i18n.t('common.amountDecimals')"
                  :class="col(12, 2)"
                />
                <div class="col-12">
                  <info-box :id="infoBoxIdEnum.INVOICE_NUMBER_FORMAT" :message="i18n.t('info.invoiceNumberFormat', { ...invoiceNumberFormatTokenParams, currentYear: dayjs().year() })" />
                </div>
              </div>
            </q-card-section>

            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                <q-icon
                  name="mdi-file-document-multiple-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.documentTemplates', 2) }}
                </span>
              </q-item-label>
              <div class="q-ml-sm q-pl-md">
                <document-term-template-select manage-mode />
              </div>
            </q-card-section>

            <template v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.REIMBURSEMENTS)">
              <q-card-section class="q-pt-none">
                <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
                  <q-icon
                    name="mdi-wallet-outline"
                    class="q-mr-sm"
                    color="primary"
                    size="sm"
                  />
                  <span class="dialog-card-section-title">{{ i18n.t('common.expenseClaims') }}</span>
                </q-item-label>
                <div class="q-ml-sm row q-col-gutter-md">
                  <q-input
                    v-model="companyData.expense_claim_number_format"
                    :label="i18n.t('common.expenseClaimNumberFormat')"
                    :rules="invoiceNumberFormatRules"
                    maxlength="50"
                    class="col-12"
                  />
                  <div class="col-12">
                    <info-box :id="infoBoxIdEnum.EXPENSE_CLAIM_SETTINGS" :message="i18n.t('info.expenseClaimSettings', { ...invoiceNumberFormatTokenParams })" />
                  </div>
                </div>
              </q-card-section>

              <q-card-section class="q-pt-none">
                <q-item-label header class="q-px-none row items-center">
                  <q-icon
                    name="mdi-tag-multiple-outline"
                    class="q-mr-sm"
                    color="primary"
                    size="sm"
                  />
                  <span class="dialog-card-section-title">
                    {{ i18n.t('common.expenseItems') }}
                  </span>
                </q-item-label>
                <div :class="{ 'q-ml-sm q-pl-md': !isMobile }">
                  <expense-item-settings />
                </div>
              </q-card-section>
            </template>
          </q-tab-panel>

          <q-tab-panel v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)" :name="companyDataTabEnum.EXCHANGE_RATES" class="q-pa-none">
            <company-exchange-rates
              v-model:reference-currency-ref="companyData.default_currency_ref"
              v-model:print-exchange-rate="companyData.print_exchange_rate"
              :saved-reference-currency-ref="accountingStore.companyData.default_currency_ref"
            />
          </q-tab-panel>

          <q-tab-panel :name="companyDataTabEnum.PROFILE" class="q-pa-none">
            <camera-gallery-input ref="cameraInput" :edit-item="avatarEdit" />
            <div v-if="isMobile" class="column items-center q-pt-lg q-pb-sm">
              <div
                class="profile-avatar"
                role="button"
                :aria-label="i18n.t('common.edit')"
                @click.stop="cameraInput.onActivateCamera()"
              >
                <avatar-image :image="avatarPreview ?? ''" size="8rem" />
                <q-btn
                  class="profile-avatar__badge"
                  round
                  unelevated
                  color="primary"
                  icon="mdi-camera"
                  size="md"
                />
              </div>
              <div class="text-h6 q-mt-md">{{ profileFullName }}</div>
              <div class="text-caption text-grey-7">{{ webSiteStore.user.email || webSiteStore.user.username }}</div>
              <q-btn
                v-if="avatarPreview"
                class="q-mt-xs"
                flat
                dense
                no-caps
                color="negative"
                icon="mdi-delete-outline"
                :label="i18n.t('common.delete')"
                @click="onAvatarRemove"
              />
            </div>
            <q-card-section class="q-pt-none">
              <q-item-label header class="q-px-none row items-center">
                <q-icon
                  name="mdi-account-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">
                  {{ i18n.t('common.mainInfo') }}
                </span>
              </q-item-label>
              <div class="q-ml-sm row items-center q-col-gutter-md">
                <div v-if="!isMobile" class="col-auto row items-center no-wrap q-gutter-sm">
                  <q-btn
                    v-if="!avatarPreview"
                    icon="mdi-camera"
                    round
                    @click.stop="cameraInput.onActivateCamera()"
                  />
                  <q-avatar
                    v-else
                    size="3.5rem"
                    class="cursor-pointer"
                    @click.stop="cameraInput.onActivateCamera()"
                  >
                    <q-img loading="lazy" fit="cover" :src="avatarPreview">
                      <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                        <q-icon name="mdi-camera" size="sm" />
                      </div>
                    </q-img>
                  </q-avatar>
                  <q-btn
                    v-if="avatarPreview"
                    flat
                    round
                    icon="mdi-delete-outline"
                    color="negative"
                    @click="onAvatarRemove"
                  >
                    <q-tooltip>{{ i18n.t('common.delete') }}</q-tooltip>
                  </q-btn>
                </div>
                <div class="col row q-col-gutter-md">
                  <q-input
                    v-model="profile.first_name"
                    :label="i18n.t('common.firstName')"
                    maxlength="150"
                    :class="col(6, 3)"
                  />
                  <q-input
                    v-model="profile.last_name"
                    :label="i18n.t('common.lastName')"
                    maxlength="150"
                    :class="col(6, 3)"
                  />
                  <q-input
                    v-if="!isMobile"
                    :model-value="webSiteStore.user.email || webSiteStore.user.username"
                    :label="i18n.t('common.email')"
                    :class="col(12, 3)"
                    readonly
                  />
                  <q-select
                    v-model="profile.preferred_language"
                    :options="portalStore.getLocalizedLanguages()"
                    :label="i18n.t('common.preferredLanguage')"
                    option-value="code"
                    option-label="name"
                    emit-value
                    map-options
                    :class="col(12, 3)"
                  />
                </div>
              </div>
            </q-card-section>
            <q-card-section v-if="webSiteStore.hasStaff" class="q-pt-none">
              <q-item-label header class="q-px-none row items-center">
                <q-icon
                  name="mdi-wallet-outline"
                  class="q-mr-sm"
                  color="primary"
                  size="sm"
                />
                <span class="dialog-card-section-title">{{ i18n.t('common.reimbursements') }}</span>
              </q-item-label>
              <div class="q-ml-sm">
                <staff-expense-profile ref="reimbursementProfile" :staff-id="webSiteStore.staffId" />
              </div>
            </q-card-section>
            <notification-settings v-model="notificationPreferences" />
          </q-tab-panel>
        </q-tab-panels>
      </q-form>
      <q-card-actions
        v-if="isCompanyDataChanged || isProfileUpdated || avatarChanged || isReimbursementProfileUpdated || isNotificationPreferencesUpdated"
        :class="['align-center dialog-card-bottom-bar', { 'company-data__update-bar--inline': isMobile }]"
      >
        <q-btn
          class="large-btn"
          color="positive"
          :label="i18n.t('common.update')"
          @click="onSave"
        />
      </q-card-actions>
    </q-card>
    <q-dialog :model-value="showReportColumnTemplatesManager && isMobile" maximized @hide="onReportColumnTemplatesManagerHide">
      <report-column-templates-manager @cancel="showReportColumnTemplatesManager = false" />
    </q-dialog>
    <q-dialog :model-value="showPrintTemplatesManager && isMobile" maximized @hide="showPrintTemplatesManager = false">
      <print-templates-manager
        @cancel="showPrintTemplatesManager = false"
        @defaults-changed="syncDefaults(printTemplateDefaults)"
      />
    </q-dialog>
    <confirm-dialog
      v-model="showDeleteVatDialog"
      :message="i18n.t('common.confirmDeleteMessage', { item: vatToDelete?.name })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteVatConfirmed"
    />
  </div>
</template>
<style lang="scss" scoped>
.company-data--mobile {
  height: calc(100vh - 4rem);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}
.company-data--mobile > .q-card,
.company-data__tabs {
  min-width: 0;
  max-width: 100%;
}
// Lets the absolute update bar anchor to the q-page (bottom of the screen) instead of the card.
.company-data__manager {
  height: calc(100vh - 8rem);
}
.company-data__card--desktop {
  position: static;
}
.company-data__update-bar--inline {
  position: static;
}
.profile-avatar {
  position: relative;
  cursor: pointer;
}
.profile-avatar__badge {
  position: absolute;
  right: 0;
  bottom: 0;
  border: .125rem solid white;
}
</style>
